import Utils from './immersive_utils';

const tokenDecimals: Record<string, number> = {
  '1000BONK': 6,
  '1000PEPE': 6,
  '1000SHIB': 6,
  AAVE: 2,
  ADA: 4,
  ALGO: 3,
  APE: 4,
  APT: 4,
  ATOM: 2,
  AVAX: 2,
  BCH: 2,
  BERA: 4,
  BNB: 2,
  BTC: 2,
  BZR: 2,
  CAKE: 2,
  CHZ: 5,
  COMP: 2,
  CORE: 5,
  CRV: 4,
  DOGE: 4,
  DOT: 2,
  EIGEN: 4,
  ENA: 5,
  ETH: 2,
  FARTCOIN: 4,
  FIL: 4,
  HBAR: 5,
  HNT: 4,
  HYPE: 2,
  JUP: 4,
  KAS: 5,
  LINEA: 6,
  LINK: 2,
  LTC: 2,
  MOG: 10,
  NEAR: 2,
  ONDO: 4,
  ORDI: 2,
  PENDLE: 2,
  POL: 4,
  POPCAT: 5,
  PUMP: 6,
  RECALL: 5,
  SAND: 5,
  SHX: 6,
  SOL: 2,
  STX: 4,
  SUI: 2,
  TAC: 6,
  TON: 2,
  TRUMP: 2,
  TRX: 4,
  UNI: 2,
  VIRTUAL: 4,
  WIF: 4,
  WLD: 4,
  WLFI: 5,
  XLM: 4,
  XMR: 2,
  // XRP intentionally NOT pinned here: at a few dollars the magnitude ladder
  // gives it 4dp (matching its peers DOGE/ADA/XLM). Do not re-add it (IM-2189).
  ZEC: 2,
  ZIL: 6,
  ZK: 5,
  ZORA: 5,
};

// Order matters: longer suffixes first so "USD" doesn't steal a "USDT"/"USDC" match.
const QUOTE_SUFFIXES = ['USDT', 'USDC', 'USD'];

// Long-form currency names → short symbol. Matches `CurrencyTypeEnum`/`getCurrencyName`.
const LONG_NAME_TO_SYMBOL: Record<string, string> = {
  BITCOIN: 'BTC',
  ETHEREUM: 'ETH',
  SOLANA: 'SOL',
  RIPPLE: 'XRP',
  AVALANCHE: 'AVAX',
  TRON: 'TRX',
};

function stripQuoteSuffix(symbol: string): string {
  for (const suffix of QUOTE_SUFFIXES) {
    if (symbol.endsWith(suffix) && symbol.length > suffix.length) {
      return symbol.slice(0, -suffix.length);
    }
  }
  return symbol;
}

// Magnitude-based decimal ladder for token prices with no explicit map entry.
// Mirrors the market-data page's index-price rule (token-data-content.tsx's old
// inline handleIndexPriceCurrency block) so the two are driven by ONE source:
//   <0.1 → 6dp, 0.1–10 → 4dp, 10–100 → 3dp, 100–1000 → 2dp, >=1000 → 1dp.
// The sub-$0.10 → 6dp bucket keeps small-cap tokens legible: at 4dp a $0.00123
// name clips to 0.0012, so anything under a dime gets two more places (leaves
// $0.10–$10 at 4dp so mid-range names aren't over-padded).
// Uses abs so signed callers (transaction/position prices) bucket by magnitude;
// index prices are non-negative so this is a no-op for the market-data header.
export function getPriceDecimalsByMagnitude(value: number): number {
  const a = Math.abs(Number(value));
  if (!Number.isFinite(a)) return 2;
  if (a < 0.1) return 6;
  if (a < 10) return 4;
  if (a < 100) return 3;
  if (a < 1000) return 2;
  return 1;
}

// Map lookup only — resolves alias + concatenated-quote forms, returns undefined
// when the token isn't explicitly mapped (so callers pick their own fallback).
function lookupTokenDecimals(tokenSymbol: string): number | undefined {
  if (!tokenSymbol) return undefined;

  // Case-insensitive: uppercase everything.
  // Take the first chunk before any "_" or "-" separator (handles BTC_USDT, BTC-USD, etc.).
  const baseChunk = tokenSymbol.toUpperCase().split(/[_-]/)[0];

  // 1) Long-form name → short symbol (BITCOIN → BTC, ETHEREUM → ETH, ...).
  const aliased = LONG_NAME_TO_SYMBOL[baseChunk] ?? baseChunk;

  // 2) Direct match (BTC, VIRTUAL, ...).
  if (tokenDecimals[aliased] !== undefined) {
    return tokenDecimals[aliased];
  }

  // 3) Strip concatenated quote suffix (BTCUSDT → BTC, VIRTUALUSDC → VIRTUAL).
  const stripped = stripQuoteSuffix(aliased);
  if (tokenDecimals[stripped] !== undefined) {
    return tokenDecimals[stripped];
  }

  return undefined;
}

// Value-independent per-token decimals. Kept as a flat-2 fallback for any caller
// that only has a symbol (no value to bucket by). Price-aware rendering should
// prefer formatTokenPrice, which falls back to the magnitude ladder instead.
export function getTokenDecimals(tokenSymbol: string): number {
  return lookupTokenDecimals(tokenSymbol) ?? 2;
}

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

function toSubscriptNotation(value: number, decimals: number): string | null {
  if (decimals < 8 || !Number.isFinite(value) || Math.abs(value) >= 1 || value === 0) {
    return null;
  }

  const fraction = Math.abs(value).toFixed(decimals).split('.')[1];
  const withoutLeading = fraction.replace(/^0+/, '');
  const zeros = fraction.length - withoutLeading.length;
  const significant = withoutLeading.replace(/0+$/, '');

  if (zeros < 4 || !significant) return null;

  const subscript = String(zeros)
    .split('')
    .map((digit) => SUBSCRIPT_DIGITS[Number(digit)])
    .join('');

  return `${value < 0 ? '-' : ''}0.0${subscript}${significant}`;
}

export function formatTokenPrice(value: number, tokenSymbol: string, asCurrency = false): string {
  // Explicit map wins (BTC → 2dp); unmapped tokens (e.g. MORPHO) fall back to
  // the magnitude ladder rather than a flat 2dp, matching the market-data page.
  // getPriceDecimalsByMagnitude coerces internally, so no local Number() needed.
  const decimals = lookupTokenDecimals(tokenSymbol) ?? getPriceDecimalsByMagnitude(value);
  const subscript = toSubscriptNotation(value, decimals);

  if (subscript) return asCurrency ? `$${subscript}` : subscript;

  return asCurrency ? Utils.valueToCurrency(value, decimals) : Utils.formatNumber(value, decimals);
}
